import React, { useMemo } from 'react';
import MermaidDiagram from './MermaidDiagram';

type DiagramNode = {
  id: string;
  title: string;
  members: string[];
  x: number;
  y: number;
  width: number;
  stereotype?: string;
};

type DiagramEdge = {
  from: string;
  to: string;
  dashed?: boolean;
};

type ComponentDiagramData = {
  width: number;
  height: number;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
};

const escapeXml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

// Keep the class body readable at both normal and fullscreen sizes. The
// header, first member, line rhythm, and bottom padding use fixed geometry so
// every card has the same internal breathing room regardless of member count.
const CARD_HEADER_HEIGHT = 54;
const CARD_BODY_TOP_PADDING = 20;
const CARD_MEMBER_LINE_HEIGHT = 30;
const CARD_BODY_BOTTOM_PADDING = 20;
const CARD_HORIZONTAL_PADDING = 22;
const nodeHeight = (node: DiagramNode) => (
  CARD_HEADER_HEIGHT
  + CARD_BODY_TOP_PADDING
  + node.members.length * CARD_MEMBER_LINE_HEIGHT
  + CARD_BODY_BOTTOM_PADDING
);

const nodeCentre = (node: DiagramNode) => ({
  x: node.x + node.width / 2,
  y: node.y + nodeHeight(node) / 2,
});

const pointOnNodeEdge = (from: DiagramNode, to: DiagramNode) => {
  const source = nodeCentre(from);
  const destination = nodeCentre(to);
  const dx = destination.x - source.x;
  const dy = destination.y - source.y;
  const halfWidth = from.width / 2;
  const halfHeight = nodeHeight(from) / 2;
  const scale = 1 / Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
  return { x: source.x + dx * scale, y: source.y + dy * scale };
};

const buildSvg = (data: ComponentDiagramData) => {
  const byId = new Map(data.nodes.map((node) => [node.id, node]));
  const edges = data.edges.map((edge) => {
    const from = byId.get(edge.from);
    const to = byId.get(edge.to);
    if (!from || !to) return '';
    const start = pointOnNodeEdge(from, to);
    const end = pointOnNodeEdge(to, from);
    return `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="#93a4b8" stroke-width="2" ${edge.dashed ? 'stroke-dasharray="7 5"' : ''} marker-end="url(#component-arrow)" />`;
  }).join('');

  const nodes = data.nodes.map((node) => {
    const height = nodeHeight(node);
    const stereotype = node.stereotype
      ? `<text x="${node.x + node.width - CARD_HORIZONTAL_PADDING}" y="${node.y + 33}" fill="#88dcbf" font-size="12" font-family="ui-sans-serif, system-ui" text-anchor="end">${escapeXml(node.stereotype)}</text>`
      : '';
    const members = node.members.map((member, index) => (
      `<text x="${node.x + CARD_HORIZONTAL_PADDING}" y="${node.y + CARD_HEADER_HEIGHT + CARD_BODY_TOP_PADDING + 18 + index * CARD_MEMBER_LINE_HEIGHT}" fill="#dce6f0" font-size="15" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" text-anchor="start">${escapeXml(member)}</text>`
    )).join('');
    return `<g>
      <rect x="${node.x}" y="${node.y}" width="${node.width}" height="${height}" rx="9" fill="#111b2d" stroke="#42c59a" stroke-width="2" />
      <rect x="${node.x}" y="${node.y}" width="${node.width}" height="${CARD_HEADER_HEIGHT}" rx="8" fill="#153f36" />
      <path d="M ${node.x} ${node.y + CARD_HEADER_HEIGHT} H ${node.x + node.width}" stroke="#42c59a" stroke-width="2" />
      <text x="${node.x + CARD_HORIZONTAL_PADDING}" y="${node.y + 34}" fill="#f3f8fb" font-size="16" font-family="ui-sans-serif, system-ui" font-weight="700" text-anchor="start">${escapeXml(node.title)}</text>
      ${stereotype}
      ${members}
    </g>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${data.width} ${data.height}" role="img" aria-label="Rate limiter component diagram">
    <defs><marker id="component-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L0,6 L9,3 z" fill="#93a4b8" /></marker></defs>
    ${edges}${nodes}
  </svg>`;
};

/** A reusable UML-style component diagram with consistently left-aligned members. */
const ComponentDiagram: React.FC<{ definition: string }> = ({ definition }) => {
  const svgMarkup = useMemo(() => {
    try {
      return buildSvg(JSON.parse(definition) as ComponentDiagramData);
    } catch {
      return '';
    }
  }, [definition]);

  if (!svgMarkup) return null;
  return <MermaidDiagram svgMarkup={svgMarkup} ariaLabel="Expanded component diagram" />;
};

export default ComponentDiagram;
